import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { AccountingWizardPanel } from "./AccountingWizardPanel"
import { SplitWizPanel } from "./SplitWizPanel"
import { CodingWizRunForm } from "./CodingWizRunForm"
import { run_accounting_wizard_stream } from "@/api/accounting_wizard"
import type { AccountingWizardOutcome } from "@/api/accounting_wizard"
import { run_split_wizard_stream } from "@/api/split_wizard"
import type { SplitWizOutcome } from "@/api/split_wizard"
import { fetch_wizard_outcome_cache } from "@/api/wizard_outcome_cache"
import { toast } from "@/components/ui/use-toast"
import { save_coding_wiz_preferences, use_coding_wiz_preferences } from "@/api/coding_wiz_preferences"
import {
  coding_wiz_run_mode,
  getDefaultCodingWizPreferences,
  resolve_coding_wiz_feedback,
  type CodingWizPreferences,
  type CodingWizRunMode,
} from "./coding_wiz_preferences"
import { useEffect, useRef, useState } from "preact/hooks"

export interface CodingWizSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  deductionId: string | null
  onComplete?: () => void
}

const LEDGERWIZ_CACHE_KEY = "ledgerwiz_last_outcomes_v1"
const SPLITWIZ_CACHE_KEY = "splitwiz_last_outcomes_v1"

export function CodingWizSheet({ open, onOpenChange, deductionId, onComplete }: CodingWizSheetProps) {
  const { prefs: team_prefs } = use_coding_wiz_preferences()

  const [form_prefs, set_form_prefs] = useState<CodingWizPreferences>(getDefaultCodingWizPreferences())
  const [last_run_mode, set_last_run_mode] = useState<CodingWizRunMode | null>(null)
  const [ledger_outcome, set_ledger_outcome] = useState<AccountingWizardOutcome | null>(null)
  const [split_outcome, set_split_outcome] = useState<SplitWizOutcome | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading_status_message, set_loading_status_message] = useState<string | null>(null)
  const [streaming_thinking, set_streaming_thinking] = useState("")
  const [thinking_streaming, set_thinking_streaming] = useState(false)
  const [has_cached_outcome, set_has_cached_outcome] = useState(false)
  const stream_abort_ref = useRef<AbortController | null>(null)

  function handle_after_set_backup(_deduction_id: string) {
    if (!deductionId) {
      return
    }
    const mode = last_run_mode ?? coding_wiz_run_mode(form_prefs)
    run_wizard(deductionId, mode, resolve_coding_wiz_feedback(form_prefs, null))
  }

  function run_wizard(deduction_id: string, mode: CodingWizRunMode, feedback: string | null) {
    const abort = new AbortController()
    stream_abort_ref.current?.abort()
    stream_abort_ref.current = abort
    setLoading(true)
    setError(null)
    set_loading_status_message(null)
    set_streaming_thinking("")
    set_thinking_streaming(false)
    set_has_cached_outcome(false)
    set_last_run_mode(mode)

    const stream_options = { feedback: feedback ?? undefined }

    if (mode === "ledger") {
      void run_accounting_wizard_stream(
        deduction_id,
        {
          on_progress(message: string) {
            if (stream_abort_ref.current !== abort) return
            set_loading_status_message(message)
          },
          on_thinking_delta(content: string) {
            if (stream_abort_ref.current !== abort) return
            set_thinking_streaming(true)
            set_streaming_thinking((prev: string) => prev + content)
          },
          on_thinking_done() {
            if (stream_abort_ref.current !== abort) return
            set_thinking_streaming(false)
          },
          on_outcome(o: AccountingWizardOutcome) {
            if (stream_abort_ref.current !== abort) return
            set_thinking_streaming(false)
            setLoading(false)
            set_ledger_outcome(o)
            set_split_outcome(null)
            write_cached_outcome(LEDGERWIZ_CACHE_KEY, deduction_id, o)
            onComplete?.()
          },
          on_error(message: string) {
            if (stream_abort_ref.current !== abort) return
            set_thinking_streaming(false)
            setLoading(false)
            setError(message)
          },
        },
        stream_options,
        abort.signal
      ).catch(handle_stream_error(abort))
      return
    }

    void run_split_wizard_stream(
      deduction_id,
      {
        on_progress(message: string) {
          if (stream_abort_ref.current !== abort) return
          set_loading_status_message(message)
        },
        on_thinking_delta(content: string) {
          if (stream_abort_ref.current !== abort) return
          set_thinking_streaming(true)
          set_streaming_thinking((prev: string) => prev + content)
        },
        on_thinking_done() {
          if (stream_abort_ref.current !== abort) return
          set_thinking_streaming(false)
        },
        on_outcome(o: SplitWizOutcome) {
          if (stream_abort_ref.current !== abort) return
          set_thinking_streaming(false)
          setLoading(false)
          set_split_outcome(o)
          set_ledger_outcome(null)
          write_cached_outcome(SPLITWIZ_CACHE_KEY, deduction_id, o)
          onComplete?.()
        },
        on_error(message: string) {
          if (stream_abort_ref.current !== abort) return
          set_thinking_streaming(false)
          setLoading(false)
          setError(message)
        },
      },
      stream_options,
      abort.signal
    ).catch(handle_stream_error(abort))
  }

  function handle_stream_error(abort: AbortController) {
    return (err: unknown) => {
      if (stream_abort_ref.current !== abort) return
      set_thinking_streaming(false)
      setLoading(false)
      if (err instanceof Error && err.name === "AbortError") {
        return
      }
      setError(err instanceof Error ? err.message : "Request failed")
    }
  }

  function handle_run(prefs: CodingWizPreferences, instructions: string, save_preferences: boolean) {
    if (!deductionId) {
      return
    }
    if (save_preferences) {
      void save_coding_wiz_preferences(prefs).then((ok: boolean) => {
        if (!ok) toast({ title: "Could not save CodingWiz defaults", variant: "destructive" })
      })
    }
    set_form_prefs(prefs)
    const mode = coding_wiz_run_mode(prefs)
    const feedback = resolve_coding_wiz_feedback(prefs, instructions)
    run_wizard(deductionId, mode, feedback)
  }

  useEffect(() => {
    if (!open || !deductionId) {
      stream_abort_ref.current?.abort()
      stream_abort_ref.current = null
      if (!open) {
        set_ledger_outcome(null)
        set_split_outcome(null)
        setError(null)
        set_last_run_mode(null)
      }
      return
    }

    let cancelled = false
    set_form_prefs(team_prefs)

    setLoading(false)
    setError(null)
    set_loading_status_message(null)
    set_streaming_thinking("")
    set_thinking_streaming(false)
    set_ledger_outcome(null)
    set_split_outcome(null)
    set_has_cached_outcome(false)

    void (async () => {
      try {
        const remote_split = await fetch_wizard_outcome_cache(deductionId, "split_wiz")
        if (cancelled) return
        if (remote_split.outcome != null && remote_split.saved_at != null) {
          set_split_outcome(remote_split.outcome as SplitWizOutcome)
          set_last_run_mode("split")
          set_has_cached_outcome(true)
          write_cached_outcome(SPLITWIZ_CACHE_KEY, deductionId, remote_split.outcome)
          return
        }
      } catch {
        // fall through
      }

      try {
        const remote_ledger = await fetch_wizard_outcome_cache(deductionId, "ledger_wiz")
        if (cancelled) return
        if (remote_ledger.outcome != null && remote_ledger.saved_at != null) {
          set_ledger_outcome(remote_ledger.outcome as AccountingWizardOutcome)
          set_last_run_mode("ledger")
          set_has_cached_outcome(true)
          write_cached_outcome(LEDGERWIZ_CACHE_KEY, deductionId, remote_ledger.outcome)
          return
        }
      } catch {
        // fall through
      }

      if (cancelled) return
      const cached_split = read_cached_outcome<SplitWizOutcome>(SPLITWIZ_CACHE_KEY, deductionId)
      if (cached_split) {
        set_split_outcome(cached_split)
        set_last_run_mode("split")
        set_has_cached_outcome(true)
        return
      }
      const cached_ledger = read_cached_outcome<AccountingWizardOutcome>(LEDGERWIZ_CACHE_KEY, deductionId)
      if (cached_ledger) {
        set_ledger_outcome(cached_ledger)
        set_last_run_mode("ledger")
        set_has_cached_outcome(true)
      }
    })()

    return () => {
      cancelled = true
      stream_abort_ref.current?.abort()
    }
  }, [open, deductionId, team_prefs])

  const active_mode = last_run_mode ?? coding_wiz_run_mode(form_prefs)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent class="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>CodingWiz</SheetTitle>
          <SheetDescription>
            AI-assisted coding for this transaction. Choose a single ledger code or split into multiple lines using your
            backup and org field settings.
          </SheetDescription>
        </SheetHeader>
        <div class="mt-4 space-y-4">
          {error ? <p class="text-sm text-red-600">{error}</p> : null}
          <CodingWizRunForm initial_prefs={form_prefs} is_running={loading} on_run={handle_run} />
          {has_cached_outcome ? (
            <p class="text-xs text-gray-500">Showing previous result — adjust options above and run again to refresh.</p>
          ) : null}
          {active_mode === "ledger" ? (
            <AccountingWizardPanel
              outcome={ledger_outcome}
              isLoading={loading}
              loading_status_message={loading_status_message}
              streaming_thinking={streaming_thinking}
              thinking_streaming={thinking_streaming}
              onAfterSetBackup={handle_after_set_backup}
              onRecoded={(o: AccountingWizardOutcome) => {
                set_ledger_outcome(o)
                set_split_outcome(null)
                if (deductionId) {
                  write_cached_outcome(LEDGERWIZ_CACHE_KEY, deductionId, o)
                }
                onComplete?.()
              }}
              hasCachedOutcome={has_cached_outcome}
            />
          ) : (
            <SplitWizPanel
              outcome={split_outcome}
              isLoading={loading}
              loading_status_message={loading_status_message}
              streaming_thinking={streaming_thinking}
              thinking_streaming={thinking_streaming}
              hasCachedOutcome={has_cached_outcome}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function read_cached_outcome<T>(cache_key: string, deduction_id: string): T | null {
  if (typeof localStorage === "undefined") {
    return null
  }
  try {
    const raw = localStorage.getItem(cache_key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, { outcome: T }>
    return parsed[deduction_id]?.outcome ?? null
  } catch {
    return null
  }
}

function write_cached_outcome(cache_key: string, deduction_id: string, outcome: unknown) {
  if (typeof localStorage === "undefined") {
    return
  }
  try {
    const raw = localStorage.getItem(cache_key)
    const parsed = raw ? (JSON.parse(raw) as Record<string, { outcome: unknown; saved_at: string }>) : {}
    parsed[deduction_id] = { outcome, saved_at: new Date().toISOString() }
    localStorage.setItem(cache_key, JSON.stringify(parsed))
  } catch {
    // ignore
  }
}
